'use client';

import { reatomAsync, withAbort } from '@reatom/async';
import { action, atom } from '@reatom/core';
import { withLocalStorage } from '@reatom/persist-web-storage';
import { BACKEND_API } from '@trwl/backend.api';
import { AIRPLANE_DELIVERY_TAX } from '@trwl/constants';
import { StoreProduct } from '@trwl/entities/product/@x/model';
import {
  deleteCartItemAction,
  loadCartAction,
  saveCartItemsAction,
} from '@trwl/store/actions';
import { isAuthedAtom } from '@trwl/store/atoms';

const fastCartAtom = atom<StoreProduct[]>([], 'fastCartAtom').pipe(
  withLocalStorage('fastCartAtom'),
);
const cartAtom = atom<StoreProduct[]>([], 'cartAtom').pipe(
  withLocalStorage('cartDataAtom'),
);

const setFastCartAction = action(
  (ctx, payload: Omit<StoreProduct, 'count' | 'added_at'>) => {
    fastCartAtom(ctx, [
      {
        ...payload,
        count: 1,
        added_at: Date.now(),
      },
    ]);
  },
  'setFastCartAction',
);

const clearFastCartAction = action((ctx) => {
  fastCartAtom(ctx, []);
}, 'clearFastCartAction');

const addToCartAction = action(
  (ctx, payload: Omit<StoreProduct, 'count' | 'added_at'>) => {
    cartAtom(ctx, (state) => {
      const duplicateIndex = state.findIndex(
        (product) =>
          product.name === payload.name &&
          product.id === payload.id &&
          product.airplaneDelivery === payload.airplaneDelivery,
      );

      if (duplicateIndex !== -1) {
        state[duplicateIndex] = {
          ...state[duplicateIndex],
          count: Math.min(state[duplicateIndex].count + 1, 15),
        };
        saveCartItemsAction(ctx, {
          cartItems: [
            {
              variantId: state[duplicateIndex].id,
              airplaneDelivery: state[duplicateIndex].airplaneDelivery || false,
              count: state[duplicateIndex].count,
            },
          ],
        });
        return [...state];
      }

      saveCartItemsAction(ctx, {
        cartItems: [
          {
            variantId: payload.id,
            airplaneDelivery: payload.airplaneDelivery || false,
            count: 1,
          },
        ],
      });
      return [
        ...state,
        {
          ...payload,
          count: 1,
          added_at: Date.now(),
        },
      ];
    });
  },
  'addToCartAction',
);

const removeFromCartAction = action(
  (ctx, payload: Omit<StoreProduct, 'count' | 'added_at'>) => {
    cartAtom(ctx, (state) => {
      const duplicateIndex = state.findIndex(
        (product) =>
          product.name === payload.name &&
          product.id === payload.id &&
          product.airplaneDelivery === payload.airplaneDelivery,
      );

      if (state[duplicateIndex].count === 1) {
        deleteCartItemAction(ctx, {
          variantId: state[duplicateIndex].id,
          airplaneDelivery: state[duplicateIndex].airplaneDelivery || false,
          count: state[duplicateIndex].count,
        });
        return state.filter((product) => product.id !== payload.id);
      }

      state[duplicateIndex] = {
        ...state[duplicateIndex],
        count: state[duplicateIndex].count - 1,
      };

      saveCartItemsAction(ctx, {
        cartItems: [
          {
            variantId: state[duplicateIndex].id,
            airplaneDelivery: state[duplicateIndex].airplaneDelivery || false,
            count: state[duplicateIndex].count,
          },
        ],
      });

      return [...state];
    });
  },
  'removeFromCartAction',
);

const setCartAction = action((ctx, payload: StoreProduct[]) => {
  cartAtom(ctx, [...payload]);
  saveCartItemsAction(ctx, {
    cartItems: payload.map((item) => ({
      variantId: item.id,
      airplaneDelivery: item.airplaneDelivery || false,
      count: item.count,
    })),
  });
}, 'setCartAction');

const updateCartAction = reatomAsync(async (ctx) => {
  const cart = ctx.get(cartAtom);
  const promises = cart.map((variant) =>
    BACKEND_API.ParserApi.updatePriceByVariantId(variant.id, {
      signal: ctx.controller.signal,
    }),
  );
  await Promise.all(promises);
}).pipe(withAbort());

const loadCartDataAction = reatomAsync(async (ctx) => {
  const cart = ctx.get(cartAtom);
  const updateCart = async () => {
    const result = [...cart];
    const promises = cart.map(async (variant) => {
      const response = await BACKEND_API.VariantApi.getVariantById(variant.id, {
        signal: ctx.controller.signal,
      });
      const index = cart.findIndex((item) => item.id === variant.id);
      const price = response.data;
      if (price.stock <= 0) {
        result.splice(index, 1);
      } else {
        result[index].price = price.price || 0;
      }
    });
    await Promise.all(promises);
    return result;
  };

  if (!ctx.controller.signal.aborted) {
    if (ctx.get(isAuthedAtom)) {
      loadCartAction(ctx);
    } else {
      const updatedCart = await updateCart();
      cartAtom(ctx, updatedCart);
    }
  }
}).pipe(withAbort());

const updateFastCartAction = reatomAsync(async (ctx) => {
  const cart = ctx.get(fastCartAtom);
  const promises = cart.map((variant) =>
    BACKEND_API.ParserApi.updatePriceByVariantId(variant.id, {
      signal: ctx.controller.signal,
    }),
  );
  await Promise.all(promises);
}).pipe(withAbort());

const loadFastCartDataAction = reatomAsync(async (ctx) => {
  const cart = ctx.get(fastCartAtom);
  const result = [...cart];
  const promises = cart.map(async (variant) => {
    const response = await BACKEND_API.VariantApi.getVariantById(variant.id, {
      signal: ctx.controller.signal,
    });

    const index = result.findIndex((item) => item.id === variant.id);
    const price = response.data;
    if (price.stock <= 0) {
      result.splice(index, 1);
    } else {
      result[index].price =
        price.price + (variant.airplaneDelivery ? AIRPLANE_DELIVERY_TAX : 0);
    }
  });
  await Promise.all(promises);
  if (!ctx.controller.signal.aborted) {
    fastCartAtom(ctx, result);
  }
}).pipe(withAbort());

export {
  fastCartAtom,
  cartAtom,
  setCartAction,
  setFastCartAction,
  addToCartAction,
  removeFromCartAction,
  updateCartAction,
  loadCartDataAction,
  updateFastCartAction,
  loadFastCartDataAction,
  clearFastCartAction,
};
